/**
 * Whether this VoiceAgent has a phone connection — the design's `pc` (design sync 2026-10-09).
 *
 * One answer, read the same way by the banner at the top of the Agent page, the Phone card on the
 * Channels tab, the Campaigns tab's disabled buttons and the connect dialog:
 *
 *   · **An agent that answers calls** (every template but a survey) is *working* once a number reaches
 *     it — a number on the account's own SIP trunk bound to it, or a carrier number WeTalk issued. It
 *     is *requested* while a number request for it waits on WeTalk, and *none* otherwise.
 *   · **An agent that makes calls** (a survey) is *working* once it has a caller-ID number of its own on
 *     a verified trunk — the number people see — and *none* otherwise; the exact gap (no trunk, no
 *     caller-ID number, not verified yet) is `gap`, in `callerIdGapOf`'s words.
 *
 * Every fact is a query the rest of the console already reads (the trunk list, the agent's
 * connection, the number requests), so the banner and the card can never disagree with Numbers & SIP.
 */
import { useQuery } from "@tanstack/react-query";

import { useGateway } from "../../../../api/provider.js";
import { connectionQuery } from "../../../../api/query.js";
import { isInboundOnlyTrunkView, type SipTrunkView } from "../../../../api/slice/sip-trunk.js";
import { callerIdChoicesOf, callerIdGapOf } from "../../../campaigns/campaign-copy.js";
import { numberRequestListQuery } from "../../../numbers/number-request.js";
import { sipTrunkListQuery } from "../../../numbers/sip-trunk-query.js";
import { trunkNumbersOf, type TrunkNumberRow } from "./sip-trunk-binding.js";

export type PhoneConnectionState = "working" | "requested" | "none";

export interface PhoneConnectionView {
  readonly state: PhoneConnectionState;
  /** `true` for a survey: the number is the one people see when the agent calls them. */
  readonly outbound: boolean;
  /** The number, E.164, when one is connected. */
  readonly e164: string | null;
  /** The trunk number that carries it, when the connection is the account's own trunk. */
  readonly trunkRow: TrunkNumberRow | null;
  /** Every trunk number bound to this agent (Disconnect unbinds them). */
  readonly boundTrunkRow: readonly TrunkNumberRow[];
  /** Why a survey has nothing to call from, in words; `null` when it has. */
  readonly gap: string | null;
  /** The account's trunks, once read. */
  readonly trunk: readonly SipTrunkView[];
}

/** `undefined` while the facts are being read. */
export function usePhoneConnection(voiceAgentId: string, outbound: boolean): PhoneConnectionView | undefined {
  const gateway = useGateway();
  const trunk = useQuery(sipTrunkListQuery(gateway));
  const connection = useQuery(connectionQuery(gateway, voiceAgentId));
  const request = useQuery({ ...numberRequestListQuery(gateway), retry: false });

  if (trunk.data === undefined) return undefined;
  if (!outbound && connection.data === undefined) return undefined;
  return phoneConnectionOf({
    voiceAgentId,
    outbound,
    trunk: trunk.data,
    carrierE164:
      connection.data?.number !== null && connection.data?.number !== undefined && connection.data.number.bindingKind !== "sip_trunk_number"
        ? connection.data.number.e164
        : null,
    pendingRequest: (request.data ?? []).some((one) => one.voiceAgentId === voiceAgentId && one.state === "pending"),
  });
}

/** The rule itself, apart from the queries, so it can be tested on its own. */
export function phoneConnectionOf(input: {
  readonly voiceAgentId: string;
  readonly outbound: boolean;
  readonly trunk: readonly SipTrunkView[];
  readonly carrierE164: string | null;
  readonly pendingRequest: boolean;
}): PhoneConnectionView {
  const { bound } = trunkNumbersOf(input.trunk, input.voiceAgentId);
  if (input.outbound) {
    // plan-015 SIP (ADR 0046): a trunk that only receives calls cannot be the one a survey calls from.
    const calling = input.trunk.filter((one) => !isInboundOnlyTrunkView(one));
    const choice = callerIdChoicesOf(calling, input.voiceAgentId)[0];
    const row =
      choice === undefined ? null : (bound.find((one) => one.number.phoneNumberId === choice.phoneNumberId) ?? null);
    return {
      state: choice === undefined ? "none" : "working",
      outbound: true,
      e164: choice?.e164 ?? null,
      trunkRow: row ?? bound[0] ?? null,
      boundTrunkRow: bound,
      gap: callerIdGapOf(calling, input.voiceAgentId),
      trunk: input.trunk,
    };
  }
  const row = bound[0] ?? null;
  const e164 = row?.number.e164 ?? input.carrierE164;
  return {
    state: e164 !== null ? "working" : input.pendingRequest ? "requested" : "none",
    outbound: false,
    e164,
    trunkRow: row,
    boundTrunkRow: bound,
    gap: null,
    trunk: input.trunk,
  };
}
